import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, MessageCircle, Megaphone, CalendarCheck, BarChart3, Bot } from 'lucide-react';
import { FEATURES } from '../constants';

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 },
};

export const FeaturesSection: React.FC = () => {
  return (
    <section id="que-hacemos" className="py-24 bg-[#f2f3ff] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-emerald-400/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 py-1.5 px-5 rounded-full text-xs font-bold mb-5 uppercase tracking-[0.2em]"
            style={{
              background: 'rgba(60,179,113,0.1)',
              border: '1px solid rgba(60,179,113,0.25)',
              color: '#2d8653',
            }}
          >
            <Sparkles size={14} />
            Qué automatizamos
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-black text-[#131b2e] mb-4 font-display"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
          >
            Qué hacemos por ti
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-[#565e74] max-w-2xl mx-auto font-light leading-relaxed text-lg md:text-xl"
          >
            Todo lo que tu negocio necesita hacer y no tiene tiempo de hacer.
          </motion.p>
        </div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-6 max-w-6xl mx-auto"
        >
          {/* Card 1 - Atención (2 cols) */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.02, y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="md:col-span-2 md:row-span-1 bg-white border border-[#bdcabd]/10 hover:border-[#006d3d]/30 rounded-3xl p-8 md:p-10 relative overflow-hidden group"
          >
            <div className="relative z-10 h-full flex flex-col justify-between">
              <div className="mb-8">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.15), rgba(34, 211, 238, 0.05))',
                    border: '1px solid rgba(34, 211, 238, 0.2)',
                  }}
                >
                  <MessageCircle size={28} style={{ color: '#22d3ee' }} />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#131b2e] mb-3 font-display">
                  {FEATURES[0].title}
                </h3>
                <p className="text-[#565e74] text-lg font-light max-w-md">
                  {FEATURES[0].description}
                </p>
              </div>
              <div className="flex gap-3 flex-wrap">
                {FEATURES[0].tags?.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-2 rounded-full text-xs font-medium bg-[#f2f3ff] border border-[#bdcabd]/10 text-[#006d3d]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Card 2 - Marketing (2 rows) */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.02, y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="md:col-span-1 md:row-span-2 bg-white border border-[#bdcabd]/10 hover:border-[#006d3d]/30 rounded-3xl p-8 md:p-10 relative overflow-hidden group flex flex-col"
          >
            <div className="relative z-10 flex-grow flex flex-col">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(60, 179, 113, 0.2), rgba(60, 179, 113, 0.06))',
                  border: '1px solid rgba(60, 179, 113, 0.25)',
                }}
              >
                <Megaphone size={28} style={{ color: '#3CB371' }} />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#131b2e] mb-4 font-display">
                {FEATURES[1].title}
              </h3>
              <p className="text-[#565e74] text-lg font-light mb-8">
                {FEATURES[1].description}
              </p>
              <div className="mt-auto space-y-4">
                <div className="bg-[#f2f3ff] border border-[#bdcabd]/10 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-600 font-bold">
                    7
                  </div>
                  <div className="text-sm text-[#565e74]">Posts semanales</div>
                </div>
                <div className="bg-[#f2f3ff] border border-[#bdcabd]/10 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-600 font-bold">
                    <Bot size={18} />
                  </div>
                  <div className="text-sm text-[#565e74]">Videos con IA</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card 3 - Reservas */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.02, y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="md:col-span-1 md:row-span-1 bg-white border border-[#bdcabd]/10 hover:border-[#006d3d]/30 rounded-3xl p-8 relative overflow-hidden group"
          >
            <div className="relative z-10">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{
                  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.15), rgba(167, 139, 250, 0.05))',
                  border: '1px solid rgba(167, 139, 250, 0.2)',
                }}
              >
                <CalendarCheck size={24} style={{ color: '#a78bfa' }} />
              </div>
              <h3 className="text-xl font-bold text-[#131b2e] mb-2 font-display">
                {FEATURES[2].title}
              </h3>
              <p className="text-[#565e74] text-sm font-light">
                {FEATURES[2].description}
              </p>
            </div>
          </motion.div>

          {/* Card 4 - Panel */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.02, y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="md:col-span-1 md:row-span-1 bg-white border border-[#bdcabd]/10 hover:border-[#006d3d]/30 rounded-3xl p-8 relative overflow-hidden group"
          >
            <div className="relative z-10">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{
                  background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15), rgba(249, 115, 22, 0.05))',
                  border: '1px solid rgba(249, 115, 22, 0.2)',
                }}
              >
                <BarChart3 size={24} style={{ color: '#fb923c' }} />
              </div>
              <h3 className="text-xl font-bold text-[#131b2e] mb-2 font-display">
                {FEATURES[3].title}
              </h3>
              <p className="text-[#565e74] text-sm font-light">
                {FEATURES[3].description}
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
